import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Plus, Camera, Calendar, Cloud, Users, X, Image } from "@phosphor-icons/react";

const weatherOptions = [
    "Soleado",
    "Nublado",
    "Lluvia ligera",
    "Lluvia fuerte",
    "Tormenta",
    "Nieve",
    "Viento fuerte"
];

const DailyLogPage = () => {
    const [logs, setLogs] = useState([]);
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [uploadingPhoto, setUploadingPhoto] = useState(false);
    const [selectedLog, setSelectedLog] = useState(null);
    const [filterProject, setFilterProject] = useState("all");
    const fileInputRef = useRef(null);
    const [formData, setFormData] = useState({
        project_id: "",
        date: new Date().toISOString().split("T")[0],
        weather: "",
        workers_count: 0,
        activities: "",
        incidents: "",
        notes: ""
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [logsRes, projectsRes] = await Promise.all([
                axios.get(`${API}/daily-logs`),
                axios.get(`${API}/projects`)
            ]);
            setLogs(logsRes.data);
            setProjects(projectsRes.data);
        } catch (error) {
            toast.error("Error al cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const response = await axios.post(`${API}/daily-logs`, formData);
            toast.success("Registro creado");
            setSelectedLog(response.data);
            fetchData();
            setDialogOpen(false);
        } catch (error) {
            toast.error("Error al guardar");
        }
    };

    const handlePhotoUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file || !selectedLog) return;

        setUploadingPhoto(true);
        const formDataUpload = new FormData();
        formDataUpload.append("file", file);

        try {
            await axios.post(`${API}/daily-logs/${selectedLog.id}/photos`, formDataUpload, {
                headers: { "Content-Type": "multipart/form-data" }
            });
            toast.success("Foto subida correctamente");
            fetchData();
            // Refresh selected log
            const updatedLogs = await axios.get(`${API}/daily-logs`);
            const updated = updatedLogs.data.find(l => l.id === selectedLog.id);
            if (updated) setSelectedLog(updated);
        } catch (error) {
            toast.error("Error al subir la foto");
        } finally {
            setUploadingPhoto(false);
        }
    };

    const resetForm = () => {
        setFormData({
            project_id: "",
            date: new Date().toISOString().split("T")[0],
            weather: "",
            workers_count: 0,
            activities: "",
            incidents: "",
            notes: ""
        });
    };

    const getProjectName = (projectId) => {
        const project = projects.find(p => p.id === projectId);
        return project?.name || "Sin proyecto";
    };

    const formatDate = (dateStr) => {
        return new Date(dateStr).toLocaleDateString("es-ES", {
            weekday: "long",
            year: "numeric",
            month: "long",
            day: "numeric"
        });
    };

    const filteredLogs = logs.filter(log => {
        return filterProject === "all" || log.project_id === filterProject;
    });

    const getPhotoUrl = (path) => {
        const token = localStorage.getItem("token");
        return `${API}/photos/${path}?auth=${token}`;
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="spinner"></div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-in" data-testid="daily-log-page">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading font-bold text-2xl md:text-3xl">Diario de Obra</h1>
                    <p className="text-[#555555] mt-1">
                        Registro diario de actividades y fotos
                    </p>
                </div>
                <Dialog open={dialogOpen} onOpenChange={(open) => {
                    setDialogOpen(open);
                    if (!open) resetForm();
                }}>
                    <DialogTrigger asChild>
                        <Button className="btn-primary" data-testid="add-log-btn">
                            <Plus size={20} className="mr-2" />
                            Nuevo registro
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                        <DialogHeader>
                            <DialogTitle className="font-heading">Nuevo registro diario</DialogTitle>
                        </DialogHeader>
                        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-overline mb-2 block">Obra</Label>
                                    <Select
                                        value={formData.project_id}
                                        onValueChange={(value) => setFormData({ ...formData, project_id: value })}
                                    >
                                        <SelectTrigger className="input-industrial" data-testid="log-project-select">
                                            <SelectValue placeholder="Selecciona obra" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {projects.filter(p => p.status === "activo").map((project) => (
                                                <SelectItem key={project.id} value={project.id}>
                                                    {project.name}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="label-overline mb-2 block">Fecha</Label>
                                    <Input
                                        type="date"
                                        value={formData.date}
                                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                                        className="input-industrial"
                                        required
                                        data-testid="log-date-input"
                                    />
                                </div>
                            </div>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <Label className="label-overline mb-2 block">Clima</Label>
                                    <Select
                                        value={formData.weather}
                                        onValueChange={(value) => setFormData({ ...formData, weather: value })}
                                    >
                                        <SelectTrigger className="input-industrial" data-testid="log-weather-select">
                                            <SelectValue placeholder="Selecciona clima" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {weatherOptions.map((weather) => (
                                                <SelectItem key={weather} value={weather}>
                                                    {weather}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="label-overline mb-2 block">Trabajadores</Label>
                                    <Input
                                        type="number"
                                        min="0"
                                        value={formData.workers_count}
                                        onChange={(e) => setFormData({ ...formData, workers_count: parseInt(e.target.value) || 0 })}
                                        className="input-industrial"
                                        data-testid="log-workers-input"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Actividades realizadas</Label>
                                <Textarea
                                    value={formData.activities}
                                    onChange={(e) => setFormData({ ...formData, activities: e.target.value })}
                                    className="input-industrial min-h-[100px]"
                                    placeholder="Describe las actividades del día..."
                                    data-testid="log-activities-input"
                                />
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Incidencias</Label>
                                <Textarea
                                    value={formData.incidents}
                                    onChange={(e) => setFormData({ ...formData, incidents: e.target.value })}
                                    className="input-industrial min-h-[60px]"
                                    placeholder="Incidencias o problemas del día..."
                                    data-testid="log-incidents-input"
                                />
                            </div>
                            <div>
                                <Label className="label-overline mb-2 block">Notas adicionales</Label>
                                <Textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                    className="input-industrial"
                                    placeholder="Notas..."
                                    data-testid="log-notes-input"
                                />
                            </div>
                            <div className="flex gap-3 pt-4">
                                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                                    Cancelar
                                </Button>
                                <Button type="submit" className="btn-primary flex-1" data-testid="log-save-btn">
                                    Crear registro
                                </Button>
                            </div>
                        </form>
                    </DialogContent>
                </Dialog>
            </div>

            {/* Filter */}
            <div className="flex gap-4">
                <Select value={filterProject} onValueChange={setFilterProject}>
                    <SelectTrigger className="input-industrial w-full md:w-64" data-testid="filter-log-project">
                        <SelectValue placeholder="Filtrar por obra" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas las obras</SelectItem>
                        {projects.map((project) => (
                            <SelectItem key={project.id} value={project.id}>
                                {project.name}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>

            {/* Logs Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Logs List */}
                <div className="space-y-4">
                    <h2 className="font-heading font-bold text-lg">Registros</h2>
                    {filteredLogs.length === 0 ? (
                        <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-sm">
                            <Calendar size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                            <p className="text-[#555555]">No hay registros</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {filteredLogs.map((log) => (
                                <button
                                    key={log.id}
                                    onClick={() => setSelectedLog(log)}
                                    className={`w-full text-left card-industrial ${selectedLog?.id === log.id ? "border-[#FFD700] border-2" : ""}`}
                                    data-testid={`log-item-${log.id}`}
                                >
                                    <div className="flex items-start justify-between">
                                        <div>
                                            <p className="font-semibold">{formatDate(log.date)}</p>
                                            <p className="text-sm text-[#555555]">{getProjectName(log.project_id)}</p>
                                        </div>
                                        {log.photos?.length > 0 && (
                                            <span className="flex items-center gap-1 text-xs text-[#555555]">
                                                <Image size={14} />
                                                {log.photos.length}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-4 mt-3 text-xs text-[#555555]">
                                        {log.weather && (
                                            <span className="flex items-center gap-1">
                                                <Cloud size={14} />
                                                {log.weather}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <Users size={14} />
                                            {log.workers_count} trabajadores
                                        </span>
                                    </div>
                                    {log.activities && (
                                        <p className="mt-2 text-sm line-clamp-2">{log.activities}</p>
                                    )}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                {/* Selected Log Detail */}
                <div>
                    <h2 className="font-heading font-bold text-lg mb-4">Detalle</h2>
                    {selectedLog ? (
                        <div className="bg-white border border-[#E5E5E5] rounded-sm" data-testid="log-detail">
                            <div className="p-6 border-b border-[#E5E5E5]">
                                <div className="flex items-start justify-between mb-4">
                                    <div>
                                        <h3 className="font-heading font-bold text-xl">{formatDate(selectedLog.date)}</h3>
                                        <p className="text-[#555555]">{getProjectName(selectedLog.project_id)}</p>
                                    </div>
                                    <button
                                        onClick={() => setSelectedLog(null)}
                                        className="p-2 hover:bg-[#F8F9FA] rounded-sm"
                                    >
                                        <X size={20} />
                                    </button>
                                </div>

                                <div className="grid grid-cols-2 gap-4 text-sm">
                                    <div className="flex items-center gap-2">
                                        <Cloud size={18} className="text-[#555555]" />
                                        <span>{selectedLog.weather || "No especificado"}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <Users size={18} className="text-[#555555]" />
                                        <span>{selectedLog.workers_count} trabajadores</span>
                                    </div>
                                </div>
                            </div>

                            <div className="p-6 space-y-4">
                                {selectedLog.activities && (
                                    <div>
                                        <h4 className="label-overline mb-2">Actividades</h4>
                                        <p className="text-sm whitespace-pre-wrap">{selectedLog.activities}</p>
                                    </div>
                                )}
                                {selectedLog.incidents && (
                                    <div>
                                        <h4 className="label-overline mb-2 text-red-600">Incidencias</h4>
                                        <p className="text-sm whitespace-pre-wrap">{selectedLog.incidents}</p>
                                    </div>
                                )}
                                {selectedLog.notes && (
                                    <div>
                                        <h4 className="label-overline mb-2">Notas</h4>
                                        <p className="text-sm whitespace-pre-wrap">{selectedLog.notes}</p>
                                    </div>
                                )}

                                {/* Photos Section */}
                                <div>
                                    <div className="flex items-center justify-between mb-3">
                                        <h4 className="label-overline">Fotos</h4>
                                        <input
                                            ref={fileInputRef}
                                            type="file"
                                            accept="image/*"
                                            onChange={handlePhotoUpload}
                                            className="hidden"
                                            data-testid="photo-upload-input"
                                        />
                                        <Button
                                            size="sm"
                                            onClick={() => fileInputRef.current?.click()}
                                            disabled={uploadingPhoto}
                                            className="btn-secondary text-xs"
                                            data-testid="upload-photo-btn"
                                        >
                                            <Camera size={16} className="mr-1" />
                                            {uploadingPhoto ? "Subiendo..." : "Añadir foto"}
                                        </Button>
                                    </div>

                                    {selectedLog.photos?.length > 0 ? (
                                        <div className="photo-grid">
                                            {selectedLog.photos.map((photo, index) => (
                                                <div
                                                    key={index}
                                                    className="aspect-square bg-[#F8F9FA] rounded-sm overflow-hidden border border-[#E5E5E5]"
                                                >
                                                    <img
                                                        src={getPhotoUrl(photo)}
                                                        alt={`Foto ${index + 1}`}
                                                        className="w-full h-full object-cover"
                                                        onError={(e) => {
                                                            e.target.onerror = null;
                                                            e.target.src = "https://images.pexels.com/photos/4458205/pexels-photo-4458205.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
                                                        }}
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className="text-center py-8 bg-[#F8F9FA] rounded-sm border-2 border-dashed border-[#E5E5E5]">
                                            <Camera size={32} className="mx-auto mb-2 text-[#CCCCCC]" />
                                            <p className="text-sm text-[#555555]">No hay fotos</p>
                                            <p className="text-xs text-[#999999] mt-1">Haz clic en "Añadir foto" para subir</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="text-center py-12 bg-white border border-[#E5E5E5] rounded-sm">
                            <Calendar size={48} className="mx-auto mb-4 text-[#CCCCCC]" />
                            <p className="text-[#555555]">Selecciona un registro para ver el detalle</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DailyLogPage;
